import { useRef, useEffect, useState } from 'react';
import { LiveAudioVisualizer } from 'react-audio-visualize';
import firebase from '../../../firebase/compat';
import Tooltip from "../../common/Tooltip.jsx";
import DeccaIcon from "./DeccaIcon.jsx";

const InputContainer = ({ setMessage, message, handleKeyDown, commitMessage }) => {
  const textareaRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // State
  const [isRecording, setIsRecording] = useState(false);
  const [loading, setLoading] = useState(false);

  // auto-resize textarea on message change
  useEffect(() => {
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
    }
  }, [message]);

  const hasText = (message || '').trim().length > 0;

  const toggleRecording = async () => {
    if (!isRecording) {
      setMessage('');

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      audioChunksRef.current = [];

      mr.ondataavailable = e => audioChunksRef.current.push(e.data);
      mr.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        setLoading(true);

        await firebase.functions().httpsCallable('deccaVoiceToText')({
          base64Audio: base64,
          fileType: 'webm'
        })
          .then(res =>
            setMessage(res.data.text),
            setLoading(false)
          )
          .catch(console.error);
      };

      mr.start();
      setIsRecording(true);
    } else {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  return (
    <div id="decca-input-container" className="dz-composer">
      <div className={`dz-composer-pill${isRecording ? ' dz-composer-pill-rec' : ''}`}>
        {isRecording ? (
          <>
            <div className="dz-wave">
              <LiveAudioVisualizer
                mediaRecorder={mediaRecorderRef.current}
                height={28}
              />
            </div>
            <Tooltip content="Opname stoppen" left="-100px" top="-50px" width="25px">
              <button className="dz-ib dz-ib-round dz-ib-stop" aria-label="Opname stoppen" onClick={toggleRecording}>
                <DeccaIcon name="stop" size={16} />
              </button>
            </Tooltip>
          </>
        ) : (
          <>
            <textarea
              ref={textareaRef}
              onKeyDown={handleKeyDown}
              value={loading ? 'Omzetten naar tekst…' : message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Typ een bericht…"
              rows={1}
              className="dz-composer-input"
            />
            <Tooltip content="Spraak opnemen" left="-100px" top="-50px" width="25px">
              <button className="dz-ib dz-ib-round" aria-label="Spraak opnemen" onClick={toggleRecording}>
                <DeccaIcon name="mic" size={18} />
              </button>
            </Tooltip>
            <Tooltip content="Versturen" left="-100px" top="-50px" width="25px">
              <button
                className={`dz-ib dz-ib-round dz-ib-send${hasText ? ' dz-ib-send-on' : ''}`}
                aria-label="Versturen"
                onClick={commitMessage}
              >
                <DeccaIcon name="send" size={18} />
              </button>
            </Tooltip>
          </>
        )}
      </div>
    </div>
  );
};

export default InputContainer;
